/**
 * The Responses — Statement Sentiment (SYR-68, Phase E).
 *
 * Mirrors the existing /dashboard/analytics/statements page (fonts + layout):
 * the gradient highlights strip, the score-tagged element accordion, and the
 * reused dashboard `StatementCard` (circular gauge dials) in a responsive grid.
 * Driven by the global MarketerScope via `useScopedBrandEquity`.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  Collapse,
  Empty,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  TrophyOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  LockOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { Scale, BicepsFlexed } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getDisplayName } from '../../../utils/elementDisplayNames';
import { MarketerLoading } from '../atoms/MarketerLoading';
import { StatementCardWithModal } from '../../StatementCards';
import type { StatementData } from '../../StatementCards';
import { ScoreDistributionModal } from '../../ScoreDistributionModal';
import { useScopedBrandEquity } from '../useScopedBrandEquity';
import { useScopedNavigate } from '../useScopedNavigate';
import { MARKETER_UPGRADE_ROUTE } from '../gating/featureMatrix';
import { MarketerPageHeader } from './MarketerPageHeader';
import { MarketerResponsesNeeded } from '../components/MarketerResponsesNeeded';

const { Text } = Typography;

type SortKey =
  | 'default'
  | 'meanScore-desc'
  | 'meanScore-asc'
  | 'meanIntensityPercentage-desc'
  | 'meanIntensityPercentage-asc'
  | 'meanBrandPowerPercentage-desc'
  | 'meanBrandPowerPercentage-asc'
  | 'meanControversyPercentage-desc'
  | 'meanControversyPercentage-asc';
type ViewMode = 'cards' | 'table';

/** Full sort option set — mirrors the dashboard StatementsPage exactly. */
const SORT_OPTIONS: { value: SortKey; label: string; icon: React.ReactNode }[] = [
  { value: 'default', label: 'Default (A → Z)', icon: null },
  { value: 'meanScore-desc', label: 'Score (High → Low)', icon: <Scale size="14px" /> },
  { value: 'meanScore-asc', label: 'Score (Low → High)', icon: <Scale size="14px" /> },
  { value: 'meanIntensityPercentage-desc', label: 'Intensity (High → Low)', icon: <ThunderboltOutlined style={{ fontSize: 14 }} /> },
  { value: 'meanIntensityPercentage-asc', label: 'Intensity (Low → High)', icon: <ThunderboltOutlined style={{ fontSize: 14 }} /> },
  { value: 'meanBrandPowerPercentage-desc', label: 'Brand Power (High → Low)', icon: <BicepsFlexed size="14px" /> },
  { value: 'meanBrandPowerPercentage-asc', label: 'Brand Power (Low → High)', icon: <BicepsFlexed size="14px" /> },
  { value: 'meanControversyPercentage-desc', label: 'Controversy (High → Low)', icon: <WarningOutlined style={{ fontSize: 14 }} /> },
  { value: 'meanControversyPercentage-asc', label: 'Controversy (Low → High)', icon: <WarningOutlined style={{ fontSize: 14 }} /> },
];

/** Matches the dashboard StatementsPage colour bands exactly. */
function getMetricColor(
  percentage: number,
  type: 'polarity' | 'intensity' | 'power' | 'controversy',
): string {
  if (type === 'controversy') {
    if (percentage <= 20) return '#237804';
    if (percentage <= 40) return '#52c41a';
    if (percentage <= 60) return '#faad14';
    if (percentage <= 80) return '#fa8c16';
    return '#f5222d';
  }
  if (percentage < 0) return '#000000';
  if (percentage <= 20) return '#f5222d';
  if (percentage <= 40) return '#fa8c16';
  if (percentage <= 60) return '#faad14';
  if (percentage <= 80) return '#52c41a';
  return '#237804';
}

/** Metric icons — mirror the dashboard StatementCard. */
function getMetricIcon(
  type: 'polarity' | 'intensity' | 'power' | 'controversy',
  color: string,
  isNull: boolean,
): React.ReactNode {
  const iconColor = isNull ? '#bfbfbf' : color;
  const iconStyle = { color: iconColor, fontSize: 'inherit' };
  switch (type) {
    case 'polarity':
      return <Scale size="1em" color={iconColor} />;
    case 'intensity':
      return <ThunderboltOutlined style={iconStyle} />;
    case 'power':
      return <BicepsFlexed size="1em" color={iconColor} />;
    case 'controversy':
      return <WarningOutlined style={iconStyle} />;
    default:
      return null;
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Build a rich metric column (icon + coloured value + sub-scale header). */
function metricColumn(
  type: 'polarity' | 'intensity' | 'power' | 'controversy',
  dataIndex: string,
  label: string,
  subLabel: string,
): any {
  return {
    title: (
      <div style={{ textAlign: 'center' }}>
        <div>{label}</div>
        <div style={{ fontSize: 10, color: '#8c8c8c' }}>{subLabel}</div>
      </div>
    ),
    dataIndex,
    key: type,
    width: 110,
    align: 'center' as const,
    render: (value: any) => {
      const isNull = value == null;
      const colorBasis = type === 'controversy' ? Math.abs(value) : value;
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          {getMetricIcon(type, isNull ? '#bfbfbf' : getMetricColor(colorBasis, type), isNull)}
          <span style={{ color: isNull ? '#bfbfbf' : undefined }}>
            {!isNull ? `${value}%` : '--'}
          </span>
        </div>
      );
    },
  };
}

export const ResponsesPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const navigate = useScopedNavigate();
  const { response, isLoading, isError, error } = useScopedBrandEquity();
  const [sort, setSort] = useState<SortKey>('default');
  const [view, setView] = useState<ViewMode>('cards');
  const [modalStmt, setModalStmt] = useState<StatementData | null>(null);
  const handleResponseClick = useCallback((s: StatementData) => setModalStmt(s), []);

  const categories = useMemo<any[]>(
    () =>
      // Keep locked categories (even with no statements) so they render with an
      // "Upgrade Required" prompt rather than silently disappearing.
      (response?.questionCategoryScores ?? []).filter(
        (c: any) => (c.input?.length ?? 0) > 0 || c.locked,
      ),
    [response],
  );

  const sorted = useMemo<any[]>(() => {
    if (sort === 'default')
      return [...categories].sort((a, b) =>
        getDisplayName(a.category).localeCompare(getDisplayName(b.category)),
      );
    const [metric, direction] = sort.split('-') as [string, 'asc' | 'desc'];
    return [...categories].sort((a, b) => {
      const av = a[metric] ?? -Infinity;
      const bv = b[metric] ?? -Infinity;
      return direction === 'desc' ? bv - av : av - bv;
    });
  }, [categories, sort]);

  const highlights = useMemo(() => {
    const unlocked = categories.filter((c: any) => !c.locked && c.meanScore != null);
    if (unlocked.length === 0) return null;
    const strongest = unlocked.reduce((b: any, c: any) => (c.meanScore > b.meanScore ? c : b), unlocked[0]);
    const weakest = unlocked.reduce((w: any, c: any) => (c.meanScore < w.meanScore ? c : w), unlocked[0]);
    const cc = unlocked.filter((c: any) => c.meanControversyPercentage != null);
    const mostControversial = cc.length
      ? cc.reduce((t: any, c: any) => (c.meanControversyPercentage > t.meanControversyPercentage ? c : t), cc[0])
      : null;
    return { strongest, weakest, mostControversial };
  }, [categories]);

  if (isLoading && !response) {
    return <MarketerLoading />;
  }
  if (isError || !response) {
    return (
      <Alert
        type="error"
        message={
          (error as { data?: { message?: string } })?.data?.message ?? (
            <FormattedMessage
              id="marketerView.error.loadFailed"
              defaultMessage="Could not load brand equity data."
            />
          )
        }
      />
    );
  }

  return (
    <div>
      <MarketerPageHeader
        title={
          <FormattedMessage id="marketerView.responses.title" defaultMessage="Statement Sentiment" />
        }
        subtitle={
          <FormattedMessage
            id="marketerView.responses.sub"
            defaultMessage="Explore detailed metrics for each statement. Every statement is analysed for score, intensity, brand power, and controversy."
          />
        }
        actions={
          <Space size="middle" wrap>
            <Select<SortKey>
              value={sort}
              onChange={setSort}
              style={{ width: 250 }}
              size="middle"
              listHeight={400}
              options={SORT_OPTIONS.map((o) => ({
                value: o.value,
                label: <Space size="small">{o.icon}{o.label}</Space>,
              }))}
            />
            <Button.Group>
              <Button
                type={view === 'cards' ? 'primary' : 'default'}
                icon={<AppstoreOutlined />}
                onClick={() => setView('cards')}
              >
                <FormattedMessage id="marketerView.responses.cardView" defaultMessage="Card View" />
              </Button>
              <Button
                type={view === 'table' ? 'primary' : 'default'}
                icon={<UnorderedListOutlined />}
                onClick={() => setView('table')}
              >
                <FormattedMessage id="marketerView.responses.tableView" defaultMessage="Table View" />
              </Button>
            </Button.Group>
          </Space>
        }
      />

      {response.insufficientSampleSize ? (
        <MarketerResponsesNeeded
          totalResponses={response.totalResponses ?? 0}
          fromLabel={intl.formatMessage({
            id: 'marketerView.responses.title',
            defaultMessage: 'Statement Sentiment',
          })}
          description={intl.formatMessage(
            {
              id: 'statements.insufficientResponses.description',
              defaultMessage:
                'Statement sentiment analysis requires at least 10 survey responses. You currently have {count} response{plural}.',
            },
            {
              count: response.totalResponses ?? 0,
              plural: (response.totalResponses ?? 0) === 1 ? '' : 's',
            },
          )}
        />
      ) : (
        <>
      {/* Executive highlights strip — mirrors the dashboard statements page. */}
      {highlights && (
        <Row gutter={[16, 16]} style={{ marginBottom: 40 }}>
          <Col xs={24} md={8}>
            <HighlightCard
              tone={{ bg: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)', border: '#b7eb8f', icon: '#237804', text: '#237804', eyebrowColor: '#389e0d' }}
              icon={<TrophyOutlined style={{ color: '#fff', fontSize: 20 }} />}
              eyebrow={intl.formatMessage({ id: 'marketerView.responses.strongest', defaultMessage: 'Strongest Element' })}
              name={getDisplayName(highlights.strongest.category)}
              metric={`${Math.round(highlights.strongest.meanScore)}% Score`}
            />
          </Col>
          <Col xs={24} md={8}>
            <HighlightCard
              tone={{ bg: 'linear-gradient(135deg, #fff2e8 0%, #ffd8bf 100%)', border: '#ffbb96', icon: '#d4380d', text: '#d4380d', eyebrowColor: '#d4380d' }}
              icon={<ExclamationCircleOutlined style={{ color: '#fff', fontSize: 20 }} />}
              eyebrow={intl.formatMessage({ id: 'marketerView.responses.needsAttention', defaultMessage: 'Needs Attention' })}
              name={getDisplayName(highlights.weakest.category)}
              metric={`${Math.round(highlights.weakest.meanScore)}% Score`}
            />
          </Col>
          <Col xs={24} md={8}>
            <HighlightCard
              tone={{ bg: 'linear-gradient(135deg, #fffbe6 0%, #ffe58f 100%)', border: '#ffd666', icon: '#d48806', text: '#d48806', eyebrowColor: '#d48806' }}
              icon={<FireOutlined style={{ color: '#fff', fontSize: 20 }} />}
              eyebrow={intl.formatMessage({ id: 'marketerView.responses.mostControversial', defaultMessage: 'Most Controversial' })}
              name={highlights.mostControversial ? getDisplayName(highlights.mostControversial.category) : '—'}
              metric={highlights.mostControversial ? `${Math.round(highlights.mostControversial.meanControversyPercentage)}% Controversy` : '—'}
            />
          </Col>
        </Row>
      )}

      <Collapse
        items={sorted.map((category: any) => {
          const stmtCount = category.input?.length ?? 0;
          const locked = !!category.locked;

          // Score tag reflects the sorted metric (matches the dashboard page).
          let scoreTag: React.ReactNode = null;
          if (!locked && sort !== 'default') {
            const [metric] = sort.split('-');
            const value = category[metric];
            if (value != null) {
              const meta: Record<string, { name: string; type: 'polarity' | 'intensity' | 'power' | 'controversy' }> = {
                meanScore: { name: 'Score', type: 'polarity' },
                meanIntensityPercentage: { name: 'Intensity', type: 'intensity' },
                meanBrandPowerPercentage: { name: 'Brand Power', type: 'power' },
                meanControversyPercentage: { name: 'Controversy', type: 'controversy' },
              };
              const m = meta[metric];
              const color = getMetricColor(m.type === 'controversy' ? Math.abs(value) : value, m.type);
              scoreTag = (
                <Tag style={{ fontSize: 13, padding: '2px 10px', fontWeight: 600, backgroundColor: color + '15', borderColor: color, color }}>
                  {value < 0 ? '' : '+'}{Math.round(value)}% {m.name}
                </Tag>
              );
            }
          } else if (!locked && category.meanScore != null) {
            const color = getMetricColor(category.meanScore, 'intensity');
            scoreTag = (
              <Tag style={{ fontSize: 13, padding: '2px 10px', fontWeight: 600, backgroundColor: color + '15', borderColor: color, color }}>
                {Math.round(category.meanScore)}%
              </Tag>
            );
          }
          return {
            key: category.category,
            style: { marginBottom: 8, background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' },
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 600 }}>{getDisplayName(category.category)}</span>
                {locked && (
                  <Tag color="default" style={{ fontSize: 12 }}>
                    <LockOutlined style={{ marginRight: 4 }} />
                    {intl.formatMessage({ id: 'subscription.upgrade.required', defaultMessage: 'Upgrade Required' })}
                  </Tag>
                )}
                {scoreTag}
                {!locked && (
                  <Badge
                    count={`${stmtCount} statement${stmtCount !== 1 ? 's' : ''}`}
                    style={{ backgroundColor: '#f0f0f0', color: '#595959', fontSize: 12, fontWeight: 400, boxShadow: 'none' }}
                  />
                )}
              </div>
            ),
            children: locked ? (
              <div
                style={{
                  background:
                    'linear-gradient(135deg, rgba(156, 163, 175, 0.1) 0%, rgba(107, 114, 128, 0.15) 100%)',
                  borderRadius: 12,
                  border: '1px dashed #9ca3af',
                  padding: '32px 24px',
                  textAlign: 'center',
                }}
              >
                <LockOutlined style={{ fontSize: 32, color: '#9ca3af', marginBottom: 12 }} />
                <div style={{ marginBottom: 16 }}>
                  <Text style={{ color: '#6b7280', fontSize: 14 }}>
                    {intl.formatMessage({
                      id: 'subscription.upgrade.elementsRequireUpgradeToAccess',
                      defaultMessage: 'Some elements require an upgrade to access',
                    })}
                  </Text>
                </div>
                <Button
                  type="primary"
                  icon={<CrownOutlined />}
                  onClick={() => navigate(MARKETER_UPGRADE_ROUTE)}
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                    border: 'none',
                  }}
                >
                  {intl.formatMessage({ id: 'subscription.upgrade.button', defaultMessage: 'Upgrade' })}
                </Button>
              </div>
            ) : stmtCount === 0 ? (
                <Empty description={intl.formatMessage({ id: 'statements.noStatements', defaultMessage: 'No statements' })} />
              ) : view === 'cards' ? (
                <Row gutter={[24, 24]} style={{ padding: '8px 0' }}>
                  {category.input.map((s: any) => (
                    <Col xs={24} lg={12} xl={8} key={s.id}>
                      <StatementCardWithModal
                        statement={{
                          id: String(s.id),
                          text: s.text,
                          responseCount: s.responseCount,
                          meanScore: s.meanScore,
                          polarityPercentage: s.meanScore,
                          intensityPercentage: s.intensityPercentage,
                          brandPowerPercentage: s.brandPowerPercentage,
                          controversyPercentage: s.controversyPercentage,
                          scoreDistribution: s.scoreDistribution,
                          questionTypeId: s.questionTypeId,
                          scoringType: s.scoringType,
                        }}
                        isAdmin={false}
                      />
                    </Col>
                  ))}
                </Row>
              ) : (
                <Table
                  dataSource={category.input.map((s: any) => ({ ...s, key: s.id }))}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  columns={[
                    {
                      title: 'ID',
                      dataIndex: 'id',
                      key: 'id',
                      width: 60,
                      render: (id: any) => <Tag color="blue">{id}</Tag>,
                    },
                    {
                      title: intl.formatMessage({ id: 'statements.statementText', defaultMessage: 'Statement' }),
                      dataIndex: 'text',
                      key: 'text',
                      width: '30%',
                    },
                    {
                      title: intl.formatMessage({ id: 'statements.responses', defaultMessage: 'Responses' }),
                      dataIndex: 'responseCount',
                      key: 'responseCount',
                      width: 110,
                      align: 'center' as const,
                      render: (rc: any, s: any) => (
                        <Tag
                          color="geekblue"
                          style={{ cursor: rc > 0 ? 'pointer' : 'default', fontSize: 12 }}
                          onClick={() =>
                            rc > 0 &&
                            handleResponseClick({
                              id: String(s.id),
                              text: s.text,
                              responseCount: s.responseCount,
                              meanScore: s.meanScore,
                              polarityPercentage: s.meanScore,
                              intensityPercentage: s.intensityPercentage,
                              brandPowerPercentage: s.brandPowerPercentage,
                              controversyPercentage: s.controversyPercentage,
                              scoreDistribution: s.scoreDistribution,
                              questionTypeId: s.questionTypeId,
                              scoringType: s.scoringType,
                            })
                          }
                        >
                          {rc}
                        </Tag>
                      ),
                    },
                    {
                      title: intl.formatMessage({ id: 'statements.meanScore', defaultMessage: 'Mean Score' }),
                      dataIndex: 'meanScore',
                      key: 'meanScore',
                      width: 120,
                      align: 'center' as const,
                      render: (v: any) => (
                        <span style={{ color: v == null ? '#bfbfbf' : undefined }}>
                          {v != null ? `${v.toFixed(1)}%` : '--'}
                        </span>
                      ),
                    },
                    metricColumn('polarity', 'meanScore', intl.formatMessage({ id: 'statements.score', defaultMessage: 'Score' }), intl.formatMessage({ id: 'statements.scale.bipolar', defaultMessage: '-100% to +100%' })),
                    metricColumn('intensity', 'intensityPercentage', intl.formatMessage({ id: 'statements.intensity', defaultMessage: 'Intensity' }), intl.formatMessage({ id: 'statements.scale.positive', defaultMessage: '0-100%' })),
                    metricColumn('power', 'brandPowerPercentage', intl.formatMessage({ id: 'statements.brandPower', defaultMessage: 'Brand Power' }), intl.formatMessage({ id: 'statements.scale.positive', defaultMessage: '0-100%' })),
                    metricColumn('controversy', 'controversyPercentage', intl.formatMessage({ id: 'statements.controversy', defaultMessage: 'Controversy' }), intl.formatMessage({ id: 'statements.scale.positive', defaultMessage: '0-100%' })),
                  ]}
                />
              ),
          };
        })}
      />
        </>
      )}

      {/* Score distribution modal for the table view's Responses tag. */}
      <ScoreDistributionModal
        visible={modalStmt != null}
        onClose={() => setModalStmt(null)}
        statementText={modalStmt?.text ?? ''}
        responseCount={modalStmt?.responseCount ?? 0}
        scoreDistribution={modalStmt?.scoreDistribution ?? null}
        questionType={modalStmt?.scoringType === 'likert7_with_na' ? 'likert7_with_na' : 'likert7'}
      />
    </div>
  );
};

const HighlightCard: React.FunctionComponent<{
  tone: { bg: string; border: string; icon: string; text: string; eyebrowColor: string };
  icon: React.ReactNode;
  eyebrow: string;
  name: string;
  metric: string;
}> = ({ tone, icon, eyebrow, name, metric }) => (
  <div
    style={{
      background: tone.bg,
      border: `1px solid ${tone.border}`,
      borderRadius: 10,
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      height: '100%',
    }}
  >
    <div style={{ width: 40, height: 40, borderRadius: 10, background: tone.icon, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <Text style={{ fontSize: 12, color: tone.eyebrowColor, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {eyebrow}
      </Text>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {name}
      </div>
      <Text style={{ fontSize: 13, color: tone.text, fontWeight: 600 }}>{metric}</Text>
    </div>
  </div>
);
